import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { flatMap, isArray, isString } from 'lodash-es';
import { Popover } from 'components-antd';
import EmptyImage from 'images/notifications-empty.svg';

import {
  groupNotificationsTypes,
  notificationTypes,
  NotificationTypesForGrouping,
} from 'settings/constants/notifications';
import { convertNameToAvatarPlaceholder, showSuccessMessage } from 'helpers';
import { setNotificationsDrawerParamsEffect } from 'store/effects/drawers/notifications';
import {
  getInitialNotificationsEffect,
  markNotificationAsReadEffect,
} from 'store/effects/notifications';
import BaseNotification from '../BaseNotification';
import Notification from '../index';
import { getUserRolesMapSelector } from 'store/selectors/user';
import Title from '../../Title';
import { geNotificationsDrawerParamsSelector } from 'store/selectors/drawers/notifications';
import Slider from 'react-slick';
import { ArrowLeft } from 'components/Icons';
import { Icons as Icon } from '../Icons';

import styles from './styles.module.scss';
import { clearNotification } from 'services/sockets';
import {
  emptyNotificationAction,
  requestMarkNotificationAsClearedAction,
} from 'store/actions/notifications';
import { getNotificationsPaginationSelector } from 'store/selectors/sockets/notifications';
import { PENDING } from 'settings/constants/apiState';
import { NotificationsSkeleton } from '../../NotificationsSkeleton';

const ByDate = (props) => {
  const { className, groupedNotificationsByDate } = props;
  const dispatch = useDispatch();
  const [tab, setTab] = useState(groupNotificationsTypes.All);

  const { isClient, isAgent } = useSelector(getUserRolesMapSelector);
  const drawerParams = useSelector(geNotificationsDrawerParamsSelector);
  const { hasNextPage, nextCursor, loadingState } = useSelector(getNotificationsPaginationSelector);
  const isPending = loadingState === PENDING;
  const notificationsRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const el = notificationsRef.current;
      if (
        el &&
        el.scrollTop + el.clientHeight >= el.scrollHeight &&
        hasNextPage &&
        nextCursor &&
        !isPending
      ) {
        dispatch(
          getInitialNotificationsEffect({
            limit: 100,
            cursor: nextCursor,
            ...(dateFiltersMap[tab] ? { dateFilter: dateFiltersMap[tab] } : {}),
          }),
        );
      }
    };

    const el = notificationsRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
    };
  }, [hasNextPage, nextCursor, isPending, dispatch]);

  const maybeFetchMoreNotifications = () => {
    const el = notificationsRef.current;
    const isScrollable = () => el && el.scrollHeight > el.clientHeight;

    if (!isPending && el && hasNextPage && nextCursor && !isScrollable()) {
      dispatch(
        getInitialNotificationsEffect({
          limit: 100,
          cursor: nextCursor,
          ...(dateFiltersMap[tab] ? { dateFilter: dateFiltersMap[tab] } : {}),
        }),
      );
    }
  };

  useEffect(() => {
    maybeFetchMoreNotifications();
  }, [isPending, hasNextPage, nextCursor, dispatch]);

  const onClickHandler = (name, type) => {
    dispatch(setNotificationsDrawerParamsEffect({ currentNotificationsUser: name, type }));
  };

  const getSubtitle = (count, type) => {
    if (type === groupNotificationsTypes.Unread) {
      return `${count} new notifications`;
    }
    return `${count} notifications`;
  };

  const onMarkAllAsRead = (type, name) => {
    const notificationsIds = flatMap(groupedNotificationsByDate || [], (notificData) =>
      (notificData?.notifications?.list || [])
        .filter((item) => item?.UserName === name)
        .map((notification) => notification?.Id),
    );
    dispatch(markNotificationAsReadEffect({ ids: notificationsIds }));
  };

  const getNotifications = (groupType, key) => {
    const notifications = groupedNotificationsByDate?.[key];
    return notifications?.map((item) => {
      const { name, count, isUnread, avatarUrl, lastCreatedDate, notifications, Id } = item;

      if (count === 1 && notifications?.list?.[0]) {
        const singleNotification = notifications?.list?.[0] || {};

        return (
          <Notification
            key={singleNotification?.Id}
            {...singleNotification}
            groupType={groupType}
            groupCount={count}
            {...(NotificationTypesForGrouping.NewListing.includes(singleNotification?.Type)
              ? { groupedNotifications: notifications?.list }
              : {})}
            IsPinned={notifications?.list?.[0]?.IsPinned}
          />
        );
      }

      if ((isClient && count > 1) || (isAgent && drawerParams?.filter?.severityLevel)) {
        const types = [...new Set(notifications.list.map((li) => li.Type))];
        if (
          types.length === 1 &&
          Object.values(NotificationTypesForGrouping).flat(1).includes(types?.[0])
        ) {
          return (
            <Notification
              key={name}
              {...notifications?.list?.[0]}
              {...(NotificationTypesForGrouping.NewListing.includes(types?.[0])
                ? { groupedNotifications: notifications?.list }
                : {})}
              groupCount={count}
              groupIds={notifications?.list?.map((li) => li.Id) || []}
              groupType={groupType}
            />
          );
        }
      }

      return (
        <BaseNotification
          ids={notifications?.list?.map((li) => li.Id)}
          key={name}
          isRead={!isUnread}
          title={`${name}`}
          subtitle={getSubtitle(count, groupType)}
          date={lastCreatedDate}
          groupType={groupType}
          avatarPlaceholder={convertNameToAvatarPlaceholder(name)}
          avatarUrl={avatarUrl}
          onClick={() => onClickHandler(Id, groupType)}
          markable={false}
          onMarkAllAsRead={() => onMarkAllAsRead(groupType, name)}
          IsPinned={notifications?.list?.[0]?.IsPinned}
        />
      );
    });
  };

  const CarouselNext = (props) => {
    const { onClick, className } = props;
    return (
      <div className={className} onClick={onClick}>
        <ArrowLeft className={styles.icon} />
      </div>
    );
  };

  const CarouselPrev = (props) => {
    const { onClick, className } = props;
    return (
      <div className={className} onClick={onClick}>
        <ArrowLeft className={styles.icon} />
      </div>
    );
  };

  const settings = {
    draggable: true,
    accessibility: false,
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 2,
    nextArrow: <CarouselNext className={styles.arrows} />,
    prevArrow: <CarouselPrev className={styles.arrows} />,
  };

  const notifications = Object.keys(groupedNotificationsByDate);

  const onClear = (ids) => {
    clearNotification({ Ids: ids }, (message) => {
      showSuccessMessage(message);
      dispatch(requestMarkNotificationAsClearedAction({ Ids: ids }));
      maybeFetchMoreNotifications();
    });
  };

  const dateFiltersMap = {
    All: null,
    Pinned: null,
    Today: 'today',
    Yesterday: 'yesterday',
    ThisWeek: 'thisWeek',
    ThisMonth: 'thisMonth',
  };

  return (
    <div testid="by_date" className={styles.byDateWrapper}>
      <Slider {...settings} className={classNames(styles.pillsContainer)}>
        {Object.keys(groupNotificationsTypes)?.map((key) => (
          <div
            className={classNames(styles.pill, { [styles.active]: tab === key })}
            onClick={() => {
              setTab(key);
              dispatch(emptyNotificationAction());
              dispatch(
                getInitialNotificationsEffect({
                  limit: 100,
                  pinned: key === groupNotificationsTypes?.Pinned,
                  ...(dateFiltersMap[key] ? { dateFilter: dateFiltersMap[key] } : {}),
                }),
              );
            }}
          >
            {groupNotificationsTypes[key]}
          </div>
        ))}
      </Slider>
      <div ref={notificationsRef} className={classNames(styles.notificationsByDate, className)}>
        {notifications?.length > 0 ? (
          <>
            {notifications?.map((key) => {
              const showDate = groupedNotificationsByDate?.[key]?.some((groupItem) =>
                groupItem?.notifications?.list?.some(({ Type }) => notificationTypes?.[Type]),
              );
              return (
                <div className={classNames(styles.part)}>
                  {isString(key) && (
                    <div className={classNames(styles.titleContainer)}>
                      {showDate && <Title className={styles.title}>{key}</Title>}
                      <Popover
                        overlayClassName={styles.popoverOverlay}
                        placement="bottomLeft"
                        trigger="hover"
                        content={
                          <div className={styles.options}>
                            <ul className={styles.dropdown}>
                              <li
                                className={classNames(styles.item, styles.clear)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const ids = [];
                                  groupedNotificationsByDate?.[key].forEach((item) => {
                                    if (item.notifications?.list) {
                                      item.notifications.list.forEach((notification) => {
                                        if (notification.Id !== undefined) {
                                          ids.push(notification.Id);
                                        }
                                      });
                                    }
                                  });
                                  if (ids.length) onClear(ids);
                                }}
                              >
                                <div className={styles.icon}>
                                  <Icon variant={Icon.CLEAR} />
                                </div>
                                <span>Clear All</span>
                              </li>
                            </ul>
                          </div>
                        }
                        autoAdjustOverflow
                      >
                        <span
                          className={styles.menu}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Icon variant={Icon.MENU} />
                        </span>
                      </Popover>
                    </div>
                  )}
                  {getNotifications(tab, key)}
                </div>
              );
            })}
            {isPending && <NotificationsSkeleton pagination rowCount={2} />}
          </>
        ) : null}
        {notifications?.length < 1 ? (
          isPending ? (
            <NotificationsSkeleton pagination />
          ) : (
            <div className={styles.empty}>
              <img src={EmptyImage} />
              <p>
                <span>You’re all caught up!</span> <br />
                You have no new notifications.
              </p>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
};

ByDate.propTypes = {
  className: PropTypes.string,
  groupedNotificationsByDate: PropTypes.any.isRequired,
};

ByDate.defaultProps = {
  className: '',
};

export default ByDate;
